import { useState, useEffect } from 'react'
import { getPatientUpcomingAppointments, cancelAppointment } from '@/services/call-center-service'
import { useMedplum } from '@medplum/react'
import { useMiddlewareClient } from '@/hooks'
import { AppointmentCard } from './components/AppointmentCard'
import { Encounter, Patient } from '@medplum/fhirtypes'
import { Box, LoadingOverlay, SimpleGrid, Text } from '@mantine/core'
import { modals } from '@mantine/modals'

export const UpcomingAppointmentsGrid = ({
  patientData,
  setSelectedSlotToEdit,
}: {
  patientData: Patient
  setSelectedSlotToEdit: (slot: Encounter | null) => void
}) => {
  const medplum = useMedplum()
  const middleware = useMiddlewareClient()
  const [existingAppointments, setExistingAppointments] = useState<Encounter[] | []>([])
  const [loadingAppointments, setLoadingAppointments] = useState(false)
  async function fetchExistingAppointments() {
    setLoadingAppointments(true)
    const appointments = await getPatientUpcomingAppointments(medplum, patientData.id as string)
    setExistingAppointments(appointments)
    await setLoadingAppointments(false)
  }
  function getNameOfDoctor(appointment: Encounter) {
    if (appointment.participant && appointment.participant.length > 0) {
      const practitionerRef = appointment.participant.find((participant) =>
        participant.individual?.reference?.startsWith('Practitioner'),
      )
      if (practitionerRef && practitionerRef.individual) {
        return practitionerRef.individual.display || 'N/A'
      }
    }
    return 'N/A'
  }
  async function handleAppointmentCancelation(appointmentId: string) {
    setLoadingAppointments(true)
    const res = await cancelAppointment(middleware, appointmentId)
    if (res) {
      fetchExistingAppointments()
    }
    await setLoadingAppointments(false)
  }
  // Fetch existing appointments logic here
  const openCancelModal = (appointmentId: string) =>
    modals.openConfirmModal({
      title: 'Cancel Appointment',
      centered: true,
      children: (
        <Text size="sm">
          Are you sure you want to cancel this appointment? You won’t be able to undo this action.
        </Text>
      ),
      labels: { confirm: 'Cancel appointment', cancel: 'Keep appointment' },
      confirmProps: { color: 'red' },
      onCancel: () => null,
      onConfirm: () => handleAppointmentCancelation(appointmentId),
    })
  useEffect(() => {
    if (patientData.id) {
      fetchExistingAppointments()
    }
    setSelectedSlotToEdit(null)
  }, [patientData.id])

  return (
    <Box className="min-w-[780px]">
      <LoadingOverlay visible={loadingAppointments} />
      <Box></Box>
      <h2 className="mb-[40px] text-center text-[25px] font-semibold">Upcoming Appointments</h2>
      {existingAppointments.length === 0 ? (
        <p>No upcoming appointments.</p>
      ) : (
        <SimpleGrid spacing="md" cols={3} className="mb-10">
          {existingAppointments.map(
            (appointment) =>
              appointment.id && (
                <AppointmentCard
                  start={appointment.period?.start || 'N/A'}
                  doctorName={getNameOfDoctor(appointment)}
                  key={appointment.id}
                  appointmentId={appointment.id}
                  handleAppointmentCancelation={openCancelModal}
                  setSelectedSlotToEdit={() => {
                    setSelectedSlotToEdit(appointment)
                  }}
                />
              ),
          )}
        </SimpleGrid>
      )}
    </Box>
  )
}
